import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import 'intl-pluralrules';
import { initReactI18next } from 'react-i18next/initReactI18next';

import { i18nConfig } from '@sola/schema/lib/i18n-config.js';
import { endsWithVowel } from '@sola/utils/koreanGrammar.js';

import { LOCALE_FORMATTERS, isLocaleFormat } from './formatters';

const stripEmptyDigits = (value: number, lang?: string) => {
  const amount =
    Math.floor(value) === value
      ? Intl.NumberFormat(lang, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(value)
      : Intl.NumberFormat(lang, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        }).format(value);
  return amount;
};

const formatCurrency = (
  value: number,
  lang?: string,
  options?: {
    useNegativeParens?: boolean;
  },
) => {
  const amount = +value;
  if (Number.isNaN(amount)) {
    return '';
  }

  const { useNegativeParens } = options || {};
  const isNegative = amount < 0;

  let formattedAmount = Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  if (useNegativeParens && isNegative) {
    formattedAmount = `(${formattedAmount.replace('-', '')})`;
  }

  return formattedAmount;
};

const formatDateLong = (value: Date | string, lang?: string) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    // Seems safest to return empty string in case things get wonky.
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return date.toLocaleDateString(lang, options);
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./${language}/${namespace}.ts`),
    ),
  )
  .init({
    cleanCode: true,
    debug: false,
    defaultNS: 'common',
    fallbackLng: i18nConfig.defaultLocale,
    fallbackNS: 'common',
    nonExplicitSupportedLngs: true,
    ns: ['priority'],
    partialBundledLanguages: true,
    preload: [i18nConfig.defaultLocale],
    supportedLngs: i18nConfig.locales,
    interpolation: {
      // TODO: check with Kevin & Chris if we can do this
      // escapeValue: false, // Set to false to prevent i18next from escaping HTML entities
      format: (value, format, lang, options) => {
        if (!format) {
          return value;
        }
        const formatters = {
          [LOCALE_FORMATTERS.CURRENCY]: () =>
            // @ts-expect-error - this will be refactored in upcoming MR
            formatCurrency(value, lang, options),
          [LOCALE_FORMATTERS.CURRENCY_CUSTOM]: () =>
            stripEmptyDigits(value, lang),
          [LOCALE_FORMATTERS.DATE_LONG]: () => formatDateLong(value, lang),
          [LOCALE_FORMATTERS.VOWEL_ENDING]: () => endsWithVowel(value),
        };
        return isLocaleFormat(format) ? formatters[format]() : value;
      },
    },
  });

// TODO Save this for larger MR>
// SOLA_FORMATTERS.forEach(([name, cb]) => {
//   i18n.services?.formatter?.add(name, cb);
// });
export default i18n;
